
import React from 'react';
import { Zap } from 'lucide-react';

export const ProblemSection: React.FC = () => {
  return (
    <section className="py-32 px-6 md:px-12 bg-brandBg text-zodiac border-t border-zodiac/5 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-16 text-left">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.4em] text-accent mb-6">THE REAL PROBLEM</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tighter leading-tight max-w-4xl">
              It Isn’t Data. It’s <span className="relative inline-block">Disconnected Decisions.<span className="absolute -bottom-2 left-0 w-full h-1 bg-accent/20"></span></span>
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-12 gap-16 items-start pt-6">
            {/* Left Column: Strategic Insights/Quotes */}
            <div className="lg:col-span-6 space-y-16">
              {/* McKinsey Stat */}
              <div className="group animate-fade-in opacity-0" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
                <div className="w-10 h-1 bg-accent/30 mb-6"></div>
                <p className="text-lg md:text-xl text-zodiac/80 mb-4 font-medium leading-snug">
                  “Large organisations lose <span className="text-accent font-bold">20–30%</span> of potential revenue each year due to operational inefficiencies and poor execution.”
                </p>
                <p className="text-[10px] font-black uppercase tracking-widest text-zodiac/30">— McKinsey & Company</p>
              </div>
              
              {/* Gartner Stat */}
              <div className="group animate-fade-in opacity-0" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
                <div className="w-10 h-1 bg-accent/30 mb-6"></div>
                <p className="text-lg md:text-xl text-zodiac/80 mb-4 font-medium leading-snug">
                  “Poor decision execution costs organisations <span className="text-accent font-bold">millions annually</span> as decisions arrive too late to influence outcomes.”
                </p>
                <p className="text-[10px] font-black uppercase tracking-widest text-zodiac/30">— Gartner Research</p>
              </div>
              
              {/* HBR Stat */}
              <div className="group animate-fade-in opacity-0" style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}>
                <div className="w-10 h-1 bg-accent/30 mb-6"></div>
                <p className="text-lg md:text-xl text-zodiac/80 mb-4 font-medium leading-snug">
                  “Companies consistently fail to realise <span className="text-accent font-bold">30–40%</span> of expected value from strategic initiatives due to execution breakdowns.”
                </p>
                <p className="text-[10px] font-black uppercase tracking-widest text-zodiac/30">— Harvard Business Review</p>
              </div>
            </div>

            {/* Right Column: Core Mandate Box */}
            <div className="lg:col-span-6 lg:sticky lg:top-32 group">
              {/* Background Glow Effect */}
              <div className="absolute -inset-1.5 bg-gradient-to-r from-accent/40 to-zodiac/40 rounded-[2.5rem] blur-xl opacity-20 group-hover:opacity-40 transition duration-1000"></div>
              
              <div className="relative p-10 md:p-14 bg-zodiac rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden text-left h-full">
                {/* Decorative Internal Vector */}
                <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 opacity-[0.03] pointer-events-none">
                  <Zap className="w-64 h-64 text-white" strokeWidth={0.5} />
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center space-x-4 mb-8">
                    <div className="h-px w-10 bg-accent"></div>
                    <p className="text-[10px] font-black uppercase tracking-[0.5em] text-accent">Strategic Mandate</p>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-[1.3] mb-8">
                    Groflex ensures critical decisions translate into <span className="text-accent italic">coordinated action</span> across teams, systems, and time.
                  </h3>

                  <p className="text-white/50 font-light leading-relaxed mb-10 text-lg">
                    We bridge the gap between intelligence and execution by creating a persistent layer of governance over your existing operational stack.
                  </p>
                  
                  <div className="mt-auto flex items-center space-x-2 text-white/20">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></div>
                    <span className="text-[9px] font-black uppercase tracking-[0.3em]">Execution Governance Standard</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
